import React, { useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Camera, Loader2, Save, X, Lock, ChevronDown, ChevronUp, AlertCircle, RefreshCw, Copy } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface UserProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserProfileDialog({ open, onOpenChange }: UserProfileDialogProps) {
  const { user, profile, refreshUserData } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || user?.user_metadata?.full_name || '',
    email: profile?.email || user?.email || '',
    phone: profile?.phone || user?.user_metadata?.phone || '',
  });
  const [avatarUrl, setAvatarUrl] = useState(
    profile?.avatar_url || user?.user_metadata?.avatar_url || ''
  );

  const initials = formData.full_name
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const generateSecurePassword = () => {
    const length = 12;
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%&*';
    
    const allChars = lowercase + uppercase + numbers + symbols;
    let password = '';
    
    // Asegurar al menos un carácter de cada tipo
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Completar el resto de la contraseña
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Mezclar caracteres
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setPasswordData({
      newPassword: password,
      confirmPassword: password,
    });
    
    toast({
      title: "Contraseña generada",
      description: "Se ha generado una contraseña segura. Puedes copiarla antes de guardar.",
    });
  };

  const copyPasswordToClipboard = () => {
    if (passwordData.newPassword) {
      navigator.clipboard.writeText(passwordData.newPassword);
      toast({
        title: "Copiado",
        description: "Contraseña copiada al portapapeles",
      });
    }
  };

  const handleAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "Por favor selecciona una imagen válida",
        variant: "destructive",
      });
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Error",
        description: "La imagen debe ser menor a 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingAvatar(true);

    try {
      // Obtener company_id del contexto de autenticación
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select('company_id')
        .eq('user_id', user?.id)
        .eq('is_active', true)
        .limit(1)
        .single();

      if (!userRoles?.company_id) {
        throw new Error('No se pudo determinar la empresa del usuario');
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      // Estructura multi-tenant: avatars/{company_id}/{user_id}/filename
      const filePath = `${userRoles.company_id}/${user?.id}/${fileName}`;

      console.log('📸 Subiendo avatar a:', filePath);

      // Subir imagen a Supabase Storage (estructura multi-tenant)
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Obtener URL pública
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setAvatarUrl(urlData.publicUrl);

      toast({
        title: "Éxito",
        description: "Foto de perfil actualizada",
      });

    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al subir la imagen",
        variant: "destructive",
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    // Validar contraseña si se está cambiando
    if (showPasswordSection && (passwordData.newPassword || passwordData.confirmPassword)) {
      if (passwordData.newPassword.length < 6) {
        toast({
          title: "Error",
          description: "La contraseña debe tener al menos 6 caracteres",
          variant: "destructive",
        });
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast({
          title: "Error",
          description: "Las contraseñas no coinciden",
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);

    try {
      // Actualizar perfil en la tabla profiles usando upsert con manejo explícito de conflictos
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          avatar_url: avatarUrl,
        }, {
          onConflict: 'user_id',
          ignoreDuplicates: false
        });

      if (profileError) throw profileError;

      // Actualizar metadatos del usuario en auth
      const updateData: any = {
        data: {
          full_name: formData.full_name,
          phone: formData.phone,
          avatar_url: avatarUrl,
        }
      };

      // Si hay nueva contraseña, agregarla al update
      if (showPasswordSection && passwordData.newPassword) {
        updateData.password = passwordData.newPassword;
      }

      const { error: authError } = await supabase.auth.updateUser(updateData);

      if (authError) throw authError;

      // Refrescar datos del usuario
      await refreshUserData();

      toast({
        title: "Éxito",
        description: passwordData.newPassword 
          ? "Perfil y contraseña actualizados correctamente" 
          : "Perfil actualizado correctamente",
      });

      // Limpiar campos de contraseña
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setShowPasswordSection(false);
      
      onOpenChange(false);

    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al actualizar el perfil",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-[90vw] md:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Editar Perfil</DialogTitle>
          <DialogDescription>
            Actualiza tu información personal y foto de perfil
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Avatar */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <Avatar className="h-24 w-24">
                <AvatarImage src={avatarUrl} alt={formData.full_name} />
                <AvatarFallback className="text-lg font-semibold bg-primary/10 text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <Button
                size="sm"
                variant="outline"
                className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full p-0"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
            <p className="text-xs text-muted-foreground text-center">
              Haz clic en el ícono de cámara para cambiar tu foto
            </p>
          </div>

          {/* Formulario */}
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="full_name">Nombre Completo</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  full_name: e.target.value 
                }))}
                placeholder="Tu nombre completo"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  email: e.target.value 
                }))}
                placeholder="tu@email.com"
                disabled // El email no se puede cambiar por seguridad
              />
              <p className="text-xs text-muted-foreground">
                El email no se puede modificar por seguridad
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  phone: e.target.value 
                }))}
                placeholder="+502 1234 5678"
              />
            </div>
          </div>

          {/* Sección de cambio de contraseña */}
          <Separator />
          
          <div className="space-y-4">
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-between"
              onClick={() => setShowPasswordSection(!showPasswordSection)}
            >
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span>Cambiar Contraseña</span>
              </div>
              {showPasswordSection ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>

            {showPasswordSection && (
              <div className="space-y-4 pt-2">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    <strong>Importante:</strong> El cambio de contraseña es global y afectará tu acceso en <strong>todas las empresas</strong> donde estés registrado.
                  </AlertDescription>
                </Alert>

                {/* Botón de generar contraseña */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generateSecurePassword}
                    className="flex-1"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Generar Contraseña Segura
                  </Button>
                  {passwordData.newPassword && (
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={copyPasswordToClipboard}
                      title="Copiar contraseña"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="new_password">Nueva Contraseña</Label>
                  <Input
                    id="new_password"
                    type="text"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ 
                      ...prev, 
                      newPassword: e.target.value 
                    }))}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="confirm_password">Confirmar Contraseña</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ 
                      ...prev, 
                      confirmPassword: e.target.value 
                    }))}
                    placeholder="Repite la contraseña"
                  />
                </div>

                {passwordData.newPassword && passwordData.confirmPassword && 
                 passwordData.newPassword !== passwordData.confirmPassword && (
                  <p className="text-xs text-destructive">
                    Las contraseñas no coinciden
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || uploadingAvatar}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Guardar Cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}